import { useState, useRef, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ShieldCheck, AlertTriangle, Stethoscope, UserRound, LayoutDashboard, LogOut, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import CartDrawer from "@/components/site/CartDrawer";
import { useI18n } from "@/i18n";

function Header() {
  const [open, setOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const acctRef = useRef(null);
  const svcTimer = useRef(null);
  const nav = useNavigate();
  const loc = useLocation();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const { itemCount, setOpen: setCartOpen } = useCart();
  const go = (to) => { setOpen(false); setAcctOpen(false); setServicesOpen(false); nav(to); };
  const active = (p) => (loc.pathname === p ? "active" : "");
  const portalFor = (role) => (role === "doctor" ? "/doctor" : role === "admin" ? "/admin" : role === "support" ? "/support" : "/patient");
  const portalLabel = (role) => (role === "doctor" ? "Doctor portal" : role === "admin" ? "Admin console" : role === "support" ? "Employee dashboard" : "Patient dashboard");

  // Services dropdown: open on hover, but keep it open for 2s after the
  // cursor leaves so users have time to move to and pick an option.
  const openServices = () => { clearTimeout(svcTimer.current); setServicesOpen(true); };
  const closeServicesDelayed = () => { clearTimeout(svcTimer.current); svcTimer.current = setTimeout(() => setServicesOpen(false), 2000); };
  useEffect(() => () => clearTimeout(svcTimer.current), []);

  useEffect(() => {
    const onDoc = (e) => { if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Lock the page behind while the mobile menu is open, so touch-scrolling moves
  // the menu panel itself (which is full-height + scrollable) rather than the page.
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const signOut = async () => {
    setAcctOpen(false);
    setOpen(false);
    await logout();
    nav("/");
  };

  return (
    <header className="hd">
      <div className="hd-in">
        <div className="hd-brand" onClick={() => go("/")} data-testid="brand-home">
          <div className="hd-logo">2</div>
          <div>
            <h1>2nd Opinion</h1>
            <p>Health Consultants</p>
          </div>
        </div>

        <nav className={`hd-nav ${open ? "show" : ""}`} data-testid="main-nav">
          <a className={active("/")} onClick={() => go("/")} data-testid="nav-home">{t("nav.home")}</a>
          <div
            className={`hd-drop ${servicesOpen ? "open" : ""}`}
            onMouseEnter={openServices}
            onMouseLeave={closeServicesDelayed}
          >
            <button data-testid="nav-services" onClick={() => setServicesOpen((v) => { clearTimeout(svcTimer.current); return !v; })}>{t("nav.services")} <ChevronDown size={15} /></button>
            <div className="hd-menu">
              <a onClick={() => { setServicesOpen(false); go("/services"); }}>What we offer<small>Written opinions &amp; video consults</small></a>
              <a onClick={() => { setServicesOpen(false); go("/specialties"); }}>Specialties<small>Areas our specialists cover</small></a>
              <a onClick={() => { setServicesOpen(false); go("/services"); }}>How it works<small>From request to opinion</small></a>
              <a onClick={() => { setServicesOpen(false); go("/pharmaceuticals"); }} data-testid="nav-pharmaceuticals">Pharmaceuticals &amp; Pharmacy<small>Clinically reviewed medicines</small></a>
              <a onClick={() => { setServicesOpen(false); go("/nutraceuticals"); }} data-testid="nav-nutraceuticals">Nutraceuticals<small>Evidence-led supplements &amp; nutrition</small></a>
            </div>
          </div>
          <a className={active("/journal")} onClick={() => go("/journal")} data-testid="nav-journal">{t("nav.journal")}</a>
          <a className={active("/second-opinion")} onClick={() => go("/second-opinion")} data-testid="nav-second-opinion">{t("nav.second_opinion")}</a>
          <a className={active("/about")} onClick={() => go("/about")} data-testid="nav-about">{t("nav.about")}</a>
        </nav>

        <div className="hd-actions">
          <button
            className="hd-cart-btn"
            onClick={() => setCartOpen(true)}
            data-testid="nav-cart"
            aria-label="Open cart"
            title="Your cart"
          >
            <ShoppingBag size={18} />
            {itemCount > 0 && <span className="hd-cart-badge" data-testid="nav-cart-count">{itemCount}</span>}
          </button>
          {user !== undefined && (user ? (
            <div className="hd-account-wrap" ref={acctRef}>
              <button
                className="pg-btn pg-btn-ghost hd-account"
                onClick={() => setAcctOpen((v) => !v)}
                data-testid="nav-portal-account"
                aria-haspopup="menu"
                aria-expanded={acctOpen}
                title={`Account — ${user.name}`}
              >
                <UserRound size={16} /> {user.name} <ChevronDown size={15} className={`hd-acct-caret ${acctOpen ? "up" : ""}`} />
              </button>
              {acctOpen && (
                <div className="hd-acct-menu" role="menu" data-testid="account-menu">
                  <div className="hd-acct-head">
                    <span className="hd-acct-name">{user.name}</span>
                    <span className="hd-acct-role">{user.role}</span>
                  </div>
                  <button role="menuitem" onClick={() => go(portalFor(user.role))} data-testid="account-go-portal">
                    <LayoutDashboard size={16} /> {portalLabel(user.role)}
                  </button>
                  <button role="menuitem" className="danger" onClick={signOut} data-testid="account-signout">
                    <LogOut size={16} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="pg-btn pg-btn-ghost" onClick={() => go("/patient")} data-testid="nav-patient-login">{t("nav.patient_login")}</button>
          ))}
          <button className="pg-btn pg-btn-ink" onClick={() => go("/book")} data-testid="nav-book">{t("nav.book")}</button>
          <button className="hd-burger" onClick={() => setOpen(!open)} data-testid="nav-burger">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const nav = useNavigate();
  return (
    <footer className="ft">
      <div className="ft-in">
        <div className="ft-grid">
          <div>
            <div className="trust"><ShieldCheck size={15} /> NMC-registered specialists</div>
            <h4>2nd Opinion Health Consultants</h4>
            <p>Specialist second opinions and video consultations for patients across India — careful, independent reviews when a diagnosis matters most.</p>
            <button className="pg-btn doclogin" onClick={() => nav("/doctor")} data-testid="footer-doctor-login">
              <Stethoscope size={15} /> Doctor login
            </button>
          </div>
          <div>
            <h4>Explore</h4>
            <a onClick={() => nav("/services")}>What we offer</a>
            <a onClick={() => nav("/specialties")}>Specialties</a>
            <a onClick={() => nav("/journal")}>The Journal</a>
            <a onClick={() => nav("/second-opinion")}>Second opinion</a>
            <a onClick={() => nav("/about")}>About us</a>
          </div>
          <div>
            <h4>Contact</h4>
            <p><a href="mailto:support@2ndopinionconsult.in" data-testid="footer-support-email">support@2ndopinionconsult.in</a></p>
            <p>Phone: coming soon</p>
            <a onClick={() => nav("/book")}>Book a consultation</a>
            <a onClick={() => nav("/patient")}>Patient login</a>
            <a onClick={() => nav("/b2b")} data-testid="footer-b2b">B2B</a>
          </div>
        </div>

        <div className="emergency">
          <AlertTriangle size={22} color="#C03A2B" style={{ flex: "none", marginTop: 2 }} />
          <p><b>Not for emergencies.</b> This service is not for medical emergencies. If you or someone else is in danger, call <b>112</b> or <b>108</b>, or go to your nearest hospital immediately.</p>
        </div>

        <div className="ft-base">
          <span>© {new Date().getFullYear()} 2nd Opinion Health Consultants · Governed by the laws of India</span>
          <span>
            <a onClick={() => nav("/terms")}>Terms & Conditions</a> ·{" "}
            <a onClick={() => nav("/privacy")}>Privacy policy</a> ·{" "}
            <a onClick={() => nav("/support")} data-testid="footer-support">Support</a> ·{" "}
            <a onClick={() => nav("/admin")} data-testid="footer-admin">Admin</a>
          </span>
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="site">
      <div className="emerg-top">
        <b>NOT FOR EMERGENCIES.</b> <span className="e2">If you are facing a medical emergency, visit the nearest hospital immediately or call 112.</span>
      </div>
      <Header />
      <main><Outlet /></main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
